import { nextStepLabel } from "./format.ts";
import type { FollowUp, RealEstateCase, TimelineActivity } from "./types.ts";

export const RESERVATION_CONTROL_PURPOSE = "Kontrola rezervácie – deal alebo vysporiadanie";
export const RESERVATION_DUE_LABEL = "Končí rezervácia – rozhodnúť výsledok";

export type ReservationOutcome =
  | "fee_returned"
  | "fee_settled"
  | "extended"
  | "continues";

const createId = (prefix: string) => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const activity = (title: string, body: string, createdAt: string, followUp?: FollowUp): TimelineActivity => ({
  id: createId("activity"),
  createdAt,
  type: "stage",
  title,
  body,
  followUp
});

const slovakDate = (date: string) => {
  const [year, month, day] = date.split("-");
  return `${day}.${month}.${year}`;
};

export const isReservationControl = (caseItem: Pick<RealEstateCase, "stage" | "followUp">) =>
  caseItem.stage === "Rezervované" &&
  caseItem.followUp?.workflowId === "reservation" &&
  caseItem.followUp.actionId === "reservation_expiry";

export const reservationControlFollowUp = (reservationUntil: string, preferredTime = "09:00"): FollowUp => ({
  date: reservationUntil,
  time: preferredTime,
  purpose: RESERVATION_CONTROL_PURPOSE,
  workflowId: "reservation",
  actionId: "reservation_expiry"
});

export const startReservationWorkflow = (
  caseItem: RealEstateCase,
  reservationUntil: string,
  changedAt: string
): RealEstateCase => {
  const followUp = reservationControlFollowUp(reservationUntil, caseItem.followUp?.time || "09:00");
  return {
    ...caseItem,
    stage: "Rezervované",
    status: "Rezervované",
    reservationUntil,
    followUp,
    nextStep: nextStepLabel(followUp),
    timeline: [
      activity("Rezervované", `Rezervované do: ${slovakDate(reservationUntil)}`, changedAt, followUp),
      ...caseItem.timeline
    ],
    updatedAt: changedAt
  };
};

export const extendReservationWorkflow = (
  caseItem: RealEstateCase,
  reservationUntil: string,
  changedAt: string
): RealEstateCase => {
  const followUp = reservationControlFollowUp(reservationUntil, caseItem.followUp?.time || "09:00");
  return {
    ...caseItem,
    stage: "Rezervované",
    status: "Rezervované",
    reservationUntil,
    followUp,
    nextStep: nextStepLabel(followUp),
    timeline: [
      activity("Rezervácia predĺžená", `Nový termín: ${slovakDate(reservationUntil)}`, changedAt, followUp),
      ...caseItem.timeline
    ],
    updatedAt: changedAt
  };
};

const outcomeTitle: Record<Exclude<ReservationOutcome, "extended">, string> = {
  fee_returned: "Rezervačný poplatok vrátený",
  fee_settled: "Rezervačný poplatok vysporiadaný/prepadol",
  continues: "Obchod pokračuje – nový termín"
};

export const resolveReservationWorkflow = (
  caseItem: RealEstateCase,
  outcome: Exclude<ReservationOutcome, "extended">,
  changedAt: string,
  nextFollowUp?: FollowUp
): RealEstateCase => {
  const title = outcomeTitle[outcome];
  return {
    ...caseItem,
    stage: "Zainzerované",
    status: "Zainzerované",
    reservationUntil: undefined,
    followUp: nextFollowUp,
    nextStep: nextFollowUp ? nextStepLabel(nextFollowUp) : "Chýba ďalší krok",
    timeline: [
      activity(
        title,
        nextFollowUp ? `Nový ďalší krok: ${nextStepLabel(nextFollowUp)}.` : "Rezervácia bola vyhodnotená. Treba určiť ďalší krok.",
        changedAt,
        nextFollowUp
      ),
      ...caseItem.timeline
    ],
    updatedAt: changedAt
  };
};

export const completeReservationDealWorkflow = (
  caseItem: RealEstateCase,
  soldAt: string,
  realizedPrice: number,
  changedAt: string
): RealEstateCase => ({
  ...caseItem,
  stage: "Predané",
  status: "Predané",
  reservationUntil: undefined,
  realizedPrice,
  soldAt,
  followUp: undefined,
  nextStep: "Bez ďalšieho kroku",
  timeline: [
    {
      id: createId("activity"),
      createdAt: changedAt,
      type: "closed",
      title: "Obchod dokončený",
      body: `Realizovaná cena: ${realizedPrice.toLocaleString("sk-SK")} €. Dátum predaja: ${soldAt}.`
    },
    ...caseItem.timeline
  ],
  updatedAt: changedAt
});
